import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Spinner } from "reactstrap";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import { useNavigate } from "react-router-dom";
import CommonSearch from "Components/Common/CommonSearch";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { perPageData, showToastMessage } from "common/service/CommonService";

const ManualEntrySubjectList: React.FC = () => {
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const activeStudyID = globalStudy?.studyid
    ? Number(globalStudy.studyid)
    : Number(sessionStorage.getItem("activestudyid"));
  const studyName = globalStudy?.studyname || sessionStorage.getItem("studycode") || "";
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const { SelectAllMutation: AuditMutation } = useAuditService();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPageNumber(1);
  };

  const { SelectAllQuery } = useVolunteerEnrollmentService({
    studyid: activeStudyID,
    searchText: searchText,
  });

  const { data: queryResponse, isLoading, isFetching } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
  };

  const data: any[] = queryResponse?.data ?? [];

  const tableData = useMemo(() => {
    const offset = (currentPageNumber - 1) * perPageData;
    return data.map((item, index) => {
      const subjectNo = offset + index + 1;
      return {
        ...item,
        subjectNo,
        subjectinitials: item.subjectinitials || "",
        volunteerid: item.volunteerid || null,
        totalcount: item.totalcount,
        barcode: item.barcode || `${Math.floor(100000 + Math.random() * 900000)}0000`,
      };
    });
  }, [data, currentPageNumber]);

  const handleBarcodeClick = (row: any) => {
    const subjNo = row?.subjectno || row?.subjectNo;
    if (subjNo) {
      sessionStorage.setItem("subjectno", String(subjNo));
    }
    if (activeStudyID) {
      sessionStorage.setItem("activestudyid", String(activeStudyID));
    }
    if (studyName) {
      sessionStorage.setItem("studycode", String(studyName));
    }
    navigate("/accessioning/manual-entry/periods");
  };

  const columns: Column[] = [
    {
      key: "barcode",
      label: "Barcode",
      textCenter: true,
      render: (val: string, row: any) => (
        <span
          className="text-primary text-decoration-none fw-semibold"
          style={{ cursor: "pointer" }}
          onClick={() => handleBarcodeClick(row)}
        >
          {val || "--"}
        </span>
      ),
    },
    {
      key: "subjectno",
      label: "SubjectNo",
      textCenter: true,
      render: (val: string, row: any) => <span>{val || row.subjectNo || "--"}</span>,
    }
  ];

  const onAuditClick = () => {
    const payload = {
      modulename: "Sample Collection Manual Entry",
      studyid: activeStudyID,
    };
    AuditMutation.mutate(payload, {
      onSuccess: (res: any) => {
        if (res instanceof Blob) {
          const pdfFile = new File([res], "Sample Collection Manual Entry_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: () => {
        showToastMessage("error", "Error fetching audit logs");
      },
    });
  };

  return (
    <Card className="mb-0">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
            <CommonSearch onSearch={onSearch} searchText={searchText} />
          </div>
          <CommonButton
            module={PERMISSION_MODULES.MANUAL_ENTRY}
            action={PERMISSION_ACTIONS.AUDIT}
            color="primary"
            size="sm"
            onClick={onAuditClick}
            disabled={AuditMutation.isPending}
            outline
          >
            {AuditMutation.isPending ? (
              <Spinner size="sm" className="me-2" />
            ) : (
              <i className="ri-history-line me-1"></i>
            )}
            Audit
          </CommonButton>
        </div>

        <CommonTableIndexWise
          columns={columns}
          data={tableData}
          loading={isFetching || isLoading}
          paginationShow={false}
        />

        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size="xl"
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default ManualEntrySubjectList;
